/**
 * ROLE CATALOG — one definition of "which staff roles exist", five consumers.
 *
 * WHY THIS EXISTS. Before this file there were SIX independently hardcoded lists
 * of the role set and they disagreed:
 *
 *   database/rbac.js ROLE_PERMISSIONS ............... 20 keys  (permission truth)
 *   users_role_check (migration-installed CHECK) .... 23 roles (DB truth)
 *   src/utils/roleLabels.js VALID_ROLES ............. 15       (assignable)
 *   src/pages/RoleManagementPage.js ALL_ROLES ....... 12       (manageable)
 *   src/pages/SidebarVisibilityPage.js ALL_ROLES .... 11       (previewable)
 *   users.role actual values ........................ 11 distinct
 *
 * The consequence, measured 2026-08-05: `patient_experience` is defined with 60
 * permissions and is legal in the CHECK constraint, yet it appears in NONE of the
 * three UI lists — so it cannot be assigned, inspected or previewed. It is a
 * complete, correct role that is unreachable. Editing three arrays by hand would
 * fix that instance and guarantee the next role added misses one again.
 *
 * DELIBERATELY NOT ONE FLAT SET — the #335 lesson. In #335 three allowlists
 * NESTED and answered different questions, and collapsing them would have WIDENED
 * the two tighter recognizers: a security regression wearing a refactor's
 * clothes. The same trap is here. "Assignable" (can a human be put in this role),
 * "manageable" (does the permission-matrix tool render it) and "previewable"
 * (does the sidebar simulator render it) are three different questions, and some
 * absences are correct. So each consumer gets its own boolean, and EVERY false
 * carries a `why`. A difference between lists is now reviewable DATA instead of
 * three files a human has to diff by eye.
 *
 * ORDER IS LOAD-BEARING for `assignable`: the first 15 entries reproduce
 * VALID_ROLES in its exact existing order so the user-facing dropdown does not
 * silently reshuffle. Later additions append.
 *
 * WHY IT LIVES IN src/lib/ RATHER THAN database/. All three real consumers are
 * React files, and create-react-app refuses relative imports from outside src/
 * ("Relative imports outside of src/ are not supported") — verified, the build
 * fails outright. Node has no such restriction, so the CI conformance test reaches
 * IN from tests/ and the boundary is crossed only in the safe direction. It sits
 * beside src/lib/aiPermissions.js, the existing home for shared gate data.
 *
 * WHAT THIS FILE DOES NOT DO. It does not define, widen or narrow a single
 * permission — `ROLE_PERMISSIONS` in database/rbac.js remains the sole authority
 * for that, and this file is asserted AGAINST it rather than duplicating it (see
 * tests/unit/security/roleCatalogConformance.test.js). It also assigns nobody to
 * anything.
 */
"use strict";

/** Coarse grouping, used for display and asserted where it makes a claim. */
const TIERS = Object.freeze([
  "system", "governance", "executive", "management", "clinical",
  "research", "operations", "individual", "technical", "limited",
  "external", "undefined",
]);

/**
 * @typedef {Object} RoleEntry
 * @property {string}  key          the value stored in users.role
 * @property {string}  label        full display label
 * @property {string} [shortLabel]  compact label for dense matrix column headers
 * @property {string}  description  what the role is for
 * @property {string}  tier         one of TIERS
 * @property {boolean} assignable   render in the user-management role dropdown
 * @property {boolean} manageable   render in the Role & Permission Management tab
 * @property {boolean} previewable  render in the Sidebar Visibility simulator
 * @property {Object}  why          reason for every false flag / notable choice
 */

/** @type {RoleEntry[]} */
const ROLE_CATALOG = Object.freeze([
  // ── The 15 currently assignable, in VALID_ROLES' existing order ────────────
  {
    key: "admin", label: "Admin",
    description: "Full system access — all permissions",
    tier: "system", assignable: true, manageable: true, previewable: true, why: {},
  },
  {
    key: "sysadmin", label: "Delegated Admin (Sys Admin)", shortLabel: "Sys Admin",
    manageLabel: "Sys Admin",
    description: "User and system management — no PHI",
    tier: "system", assignable: true, manageable: true, previewable: true,
    why: {
      shortLabel: "Sidebar Visibility renders one column per role; the full label breaks the matrix layout.",
      manageLabel: "The Role & Permission Management tab has always shown \"Sys Admin\" while the assignment dropdown shows the long form. Preserved verbatim so this PR changes nothing a user sees; unifying them is a cosmetic decision for someone else to make.",
      totp: "NOT in TOTP_PRIVILEGED_ROLES ({admin, physician, patient_experience}) despite being a system tier — still excluded deliberately: forcing the only account that can repair a lockout needs a break-glass path first (#185).",
    },
  },
  {
    key: "board_of_directors", label: "Board of Directors",
    description: "Governance oversight — reports, BI, compliance, audit and calendar oversight, with export. No clinical, CRM, billing or user-management access.",
    tier: "governance", assignable: true, manageable: true, previewable: true,
    why: {
      previewable: "NEWLY TRUE. Was absent from Sidebar Visibility — recorded as a gap by task #92, i.e. an oversight rather than a policy. Closing it.",
      description:
        "CORRECTED 2026-08-06 (PR #340), a DELIBERATE user-visible text change. It read " +
        "\"Full admin privileges — sysadmin can restrict per member function\", which was " +
        "true before #229 re-scoped the role. #339 preserved the stale wording verbatim on " +
        "purpose — that PR's safety property was \"no user-visible text changes\" — which " +
        "left every admin reading the role-management tab a description that overstates the " +
        "role's reach by a wide margin. The new wording was written from the MEASURED set, " +
        "not from the re-scoping note: getPermissionsForRole('board_of_directors') returns " +
        "11 — reports:read/export, bi:read/export, compliance:read, audit:read, " +
        "calendar:oversight, dashboard:read, analytics:drilldown, plus voip:use and " +
        "video:use from the staff-wide grant loop. Note it is NOT purely read-only (two " +
        "exports and a drilldown), which is why the description says \"with export\" rather " +
        "than claiming read-only, and asserts the ABSENCES that actually hold.",
    },
  },
  {
    key: "executive", label: "Executive (CEO)",
    description: "Cross-functional read + oversight: CRM, reports, BI, dashboard, calendar oversight, audit.",
    tier: "executive", assignable: true, manageable: true, previewable: true,
    why: {
      manageable: "NEWLY TRUE. Assignable since the role was added but never added to the management tab — accumulated oversight, not policy.",
      previewable: "NEWLY TRUE. Same reason.",
    },
  },
  {
    key: "physician", label: "Physician",
    description: "Full clinical access + billing, scheduling, AI",
    tier: "clinical", assignable: true, manageable: true, previewable: true, why: {},
  },
  {
    key: "nurse", label: "Nurse",
    description: "Clinical care access — limited prescribing",
    tier: "clinical", assignable: true, manageable: true, previewable: true, why: {},
  },
  {
    key: "researcher", label: "Researcher",
    description: "Research and analytics — limited clinical",
    tier: "research", assignable: true, manageable: true, previewable: true, why: {},
  },
  {
    key: "sales", label: "Sales",
    description: "Lead-to-deposit CRM function: contacts, reports, outreach. Ends at conversion.",
    tier: "individual", assignable: true, manageable: true, previewable: true,
    why: {
      manageable: "NEWLY TRUE. Ten accounts hold this role and it was in neither admin tool — the largest single blind spot the six-list drift produced. (Re-measured in prod 2026-08-06: 10 rows, but only ONE is_active — ana.ituarte. The other nine are suspended.)",
      previewable: "NEWLY TRUE. Same reason.",
      subsetOf: "patient_experience — asserted in roleCatalogConformance.test.js, not merely claimed by `tier`.",
      crmExport:
        "crm:export REMOVED 2026-08-06 (PR #340) — Carlos's PEM role definition grants CRM " +
        "export to the Patient Experience Manager and not to sales. The asymmetry with " +
        "patient_experience, which KEEPS it, is deliberate; see database/rbac.js.",
    },
  },
  {
    key: "business", label: "Business",
    description: "Financial and CRM access — no PHI",
    tier: "operations", assignable: true, manageable: true, previewable: true, why: {},
  },
  {
    key: "concierge", label: "Concierge",
    description: "Patient-facing coordination: CRM read/update, notifications, dashboard, voice/video.",
    tier: "individual", assignable: true, manageable: true, previewable: true,
    why: {
      manageable: "NEWLY TRUE. Two active users hold it; absent from both admin tools.",
      previewable: "NEWLY TRUE. Same reason.",
    },
  },
  {
    key: "secretary", label: "Secretary",
    description: "Front desk — scheduling, limited patient access",
    tier: "operations", assignable: true, manageable: true, previewable: true, why: {},
  },
  {
    key: "programmer", label: "Programmer",
    description: "IT/Dev access — settings and audit only",
    tier: "technical", assignable: true, manageable: true, previewable: true, why: {},
  },
  {
    key: "network_engineer", label: "Network Engineer", shortLabel: "Net Eng",
    description: "Infrastructure — settings and audit only",
    tier: "technical", assignable: true, manageable: true, previewable: true,
    why: { shortLabel: "Matrix column header; the full label breaks the layout." },
  },
  {
    key: "readonly", label: "Read Only",
    description: "View-only access to clinical data",
    tier: "limited", assignable: true, manageable: true, previewable: true, why: {},
  },
  {
    key: "other", label: "Other",
    description: "Basic dashboard and communication",
    tier: "limited", assignable: true, manageable: true, previewable: true, why: {},
  },

  // ── Defined in ROLE_PERMISSIONS + legal in users_role_check, but until now
  //    absent from all three UI lists, i.e. unreachable. ──────────────────────
  {
    key: "patient_experience", label: "Patient Experience Manager", shortLabel: "PEM",
    description:
      "Manages the patient relationship from inception through treatment and after — " +
      "the relationship continues. Spans lead, consultation, treatment and post-treatment: " +
      "READ across the clinical journey (patients, encounters, labs, vitals, allergies, " +
      "medications, notes, insurance, treatment proposals) with WRITE confined to " +
      "relationship surfaces (CRM, notes, scheduling, follow-ups, reminders, email, chat). " +
      "A PEM is NOT a clinician: there is no encounters:create/update/sign.",
    tier: "management", assignable: true, manageable: true, previewable: true,
    why: {
      addition:
        "The role that motivated this file. 60 permissions in ROLE_PERMISSIONS, legal in " +
        "users_role_check, held by nobody, and invisible to all three admin tools.",
      tier:
        "ABOVE sales, per Daniel 2026-08-05. Not a claim — sales is a measured strict " +
        "SUBSET of this role (10 of 10 since Card P244 added the staff-wide feedback:submit " +
        "to both; 9 of 9 after PR #340 took crm:export off sales; it was 10 of " +
        "10), asserted in roleCatalogConformance.test.js and count-pinned in " +
        "pemPermissionSet.test.js so the prose cannot drift from the data.",
      totp:
        "Broadest patient-data reach on the platform, and now IN TOTP_PRIVILEGED_ROLES " +
        "({admin, physician, patient_experience}) on every transport — a PEM cannot run " +
        "single-factor by simply never enrolling. ⚠ Still ROLE-keyed: the same reach granted " +
        "by a per-user permission override arms nothing (#244).",
      consent:
        "⚠ The PEM obtains the patient release that makes affiliate disclosure lawful, but " +
        "no consent permission exists — the consent surface is gated by generic " +
        "crm:read/create/update, so this authority is indistinguishable from editing a " +
        "contact and every sales user holds it too. Separate PR (#186).",
    },
  },
  {
    key: "logistics", label: "Logistics",
    description: "Treatment logistics and travel coordination.",
    tier: "operations", assignable: true, manageable: true, previewable: true,
    why: { addition: "Defined in ROLE_PERMISSIONS and CHECK-legal; absent from all three UI lists." },
  },
  {
    key: "treating_physician", label: "Treating Physician", shortLabel: "Treating Phys",
    description: "Clinical-only surface (Clinic Tab + chart) with NO crm/billing/bi/PEM access (Dr. Luis, Jun 2026).",
    tier: "clinical", assignable: true, manageable: true, previewable: true,
    why: { addition: "Defined in ROLE_PERMISSIONS and CHECK-legal; absent from all three UI lists." },
  },
  {
    key: "consulting_physician", label: "Consulting Physician", shortLabel: "Consulting Phys",
    description: "Clinical read + consult contribution (Dr. Luis, Jun 2026).",
    tier: "clinical", assignable: true, manageable: true, previewable: true,
    why: { addition: "Defined in ROLE_PERMISSIONS and CHECK-legal; absent from all three UI lists." },
  },
  {
    key: "chief_medical_director", label: "Chief Medical Director", shortLabel: "CMD",
    description: "Clinical leadership — sees all records and all clinical surfaces (Dr. Luis, Jun 2026).",
    tier: "clinical", assignable: true, manageable: true, previewable: true,
    why: { addition: "Defined in ROLE_PERMISSIONS and CHECK-legal; absent from all three UI lists." },
  },

  // ── CHECK-legal but ZERO permissions: no ROLE_PERMISSIONS entry at all, so
  //    getPermissionsForRole() returns [] and hasPermission() is false for
  //    everything. Held by nobody today (measured 2026-08-05, 28 users). ───────
  {
    key: "manager", label: "Manager (undefined — grants nothing)",
    description:
      "NO PERMISSIONS DEFINED. Legal in users_role_check but absent from ROLE_PERMISSIONS, " +
      "so a user placed here can log in and reach nothing at all.",
    tier: "undefined", assignable: false, manageable: true, previewable: true,
    why: {
      assignable:
        "BLOCKED deliberately. This is the live onboarding trap: creating a Patient " +
        "Experience Manager as `manager` (the intuitive choice for the job title) yields " +
        "an account that sees nothing, and no admin tool warned about it. Keep it " +
        "unassignable until it is either defined or removed from the CHECK constraint.",
      manageable: "TRUE on purpose — the permission matrix should show it as empty rather than hide it.",
      previewable: "TRUE on purpose — the sidebar simulator renders an empty sidebar, which makes the emptiness visible instead of theoretical.",
      zeroPermission: "Intentional and asserted: exempt from the catalog↔ROLE_PERMISSIONS check via ZERO_PERMISSION_ROLES.",
    },
  },
  {
    key: "staff", label: "Staff (undefined — grants nothing)",
    description:
      "NO PERMISSIONS DEFINED. Legal in users_role_check but absent from ROLE_PERMISSIONS. " +
      "Same shape as `manager`.",
    tier: "undefined", assignable: false, manageable: true, previewable: true,
    why: {
      assignable: "BLOCKED deliberately — same reasoning as `manager`. A generic-sounding role that grants nothing is the worst kind of default.",
      manageable: "TRUE on purpose — visible as empty.",
      previewable: "TRUE on purpose — visible as empty.",
      zeroPermission: "Intentional and asserted via ZERO_PERMISSION_ROLES.",
    },
  },
  {
    key: "patientportal", label: "Patient Portal Identity",
    description:
      "NOT A STAFF ROLE. Patient-portal identity marker; portal credentials are handled " +
      "separately from workforce RBAC.",
    tier: "external", assignable: false, manageable: false, previewable: false,
    why: {
      assignable: "Portal identity, never staff-assignable. This is a POLICY absence, not drift.",
      manageable: "Carries no workforce permissions to manage.",
      previewable: "Has no staff sidebar to simulate.",
      zeroPermission: "Intentional and asserted via ZERO_PERMISSION_ROLES.",
    },
  },
]);

/**
 * Roles that are legal in users_role_check but deliberately have no
 * ROLE_PERMISSIONS entry. Exempt from direction (a) of the conformance test —
 * listed here so the exemption is explicit and reviewable rather than a silent
 * pass. Shrinking this list is progress; growing it needs a reason.
 */
const ZERO_PERMISSION_ROLES = Object.freeze(["manager", "staff", "patientportal"]);

// ── Derived consumer views. The ONLY supported way to get a role list. ────────

/** Assignable roles, as {value,label} — replaces roleLabels.js VALID_ROLES. */
const assignableRoles = () =>
  ROLE_CATALOG.filter((r) => r.assignable).map((r) => ({ value: r.key, label: r.label }));

/** Manageable roles, as {value,label,description} — replaces RoleManagementPage ALL_ROLES. */
const manageableRoles = () =>
  ROLE_CATALOG.filter((r) => r.manageable)
    .map((r) => ({ value: r.key, label: r.manageLabel || r.label, description: r.description }));

/** Previewable role keys — replaces SidebarVisibilityPage ALL_ROLES. */
const previewableRoleKeys = () => ROLE_CATALOG.filter((r) => r.previewable).map((r) => r.key);

/** Compact key→label map for dense matrix headers — replaces its ROLE_LABELS. */
const previewableRoleLabels = () =>
  Object.fromEntries(
    ROLE_CATALOG.filter((r) => r.previewable).map((r) => [r.key, r.shortLabel || r.label])
  );

/** Every key the catalog knows about. */
const allRoleKeys = () => ROLE_CATALOG.map((r) => r.key);

/** Lookup; returns undefined for an unknown key (callers decide how loud to be). */
const roleByKey = (key) => ROLE_CATALOG.find((r) => r.key === key);

module.exports = {
  ROLE_CATALOG, ZERO_PERMISSION_ROLES, TIERS,
  assignableRoles, manageableRoles, previewableRoleKeys, previewableRoleLabels,
  allRoleKeys, roleByKey,
};
